import { useState } from "react";
import { useOrgSettings } from "@/lib/org-settings-context";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Receipt,
  Search,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard as CreditCardIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface WalletTransaction {
  id: string;
  type: "CREDIT" | "DEBIT";
  amountCents: number;
  description: string | null;
  createdAt: string;
}

interface CardTransaction {
  id: string;
  type: string;
  status: string;
  amountCents: number;
  merchantName: string | null;
  authorizedAt: string;
}

export default function PortalTransactions() {
  const { formatCurrency } = useOrgSettings();
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");

  const { data: walletTxns = [], isLoading: walletLoading } = useQuery<WalletTransaction[]>({
    queryKey: ["customer-wallet-transactions"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/customer/wallet/transactions");
      if (!response.ok) throw new Error("Failed to fetch wallet transactions");
      return response.json();
    },
  });

  const { data: cardTxns = [], isLoading: cardLoading } = useQuery<CardTransaction[]>({
    queryKey: ["customer-all-card-transactions"],
    queryFn: async () => {
      const cardsRes = await apiRequest("GET", "/api/customer/cards");
      if (!cardsRes.ok) return [];
      const cards = await cardsRes.json();
      
      const allTxns: CardTransaction[] = [];
      for (const card of cards) {
        const txnRes = await apiRequest("GET", `/api/customer/cards/${card.id}/transactions`);
        if (txnRes.ok) {
          const txns = await txnRes.json();
          allTxns.push(...txns);
        }
      }
      return allTxns;
    },
  });

  const isLoading = walletLoading || cardLoading;

  // Combine and filter transactions
  const allTransactions = [
    ...walletTxns.map(t => ({ ...t, source: "wallet" as const })),
    ...cardTxns.map(t => ({ ...t, source: "card" as const })),
  ].sort((a, b) => {
    const dateA = new Date(a.createdAt || a.authorizedAt).getTime();
    const dateB = new Date(b.createdAt || b.authorizedAt).getTime();
    return dateB - dateA;
  });

  const filtered = allTransactions.filter((txn) => {
    const matchesSearch = search === "" || 
      txn.description?.toLowerCase().includes(search.toLowerCase()) ||
      (txn.source === "card" && txn.merchantName?.toLowerCase().includes(search.toLowerCase()));
    
    const matchesType = filterType === "all" || txn.source === filterType;
    
    return matchesSearch && matchesType;
  });

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Transactions</h1>
        <p className="text-muted-foreground mt-1">
          All your wallet and card activity
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filter Transactions</CardTitle>
          <CardDescription>Search and filter your activity</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search transactions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="wallet">Wallet</SelectItem>
                <SelectItem value="card">Card</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Transactions */}
      <Card>
        <CardHeader>
          <CardTitle>All Transactions ({filtered.length})</CardTitle>
          <CardDescription>Your complete transaction history</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filtered.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((txn: any) => (
                  <TableRow key={txn.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {txn.source === "wallet" ? (
                          txn.type === "CREDIT" ? (
                            <ArrowDownRight className="h-4 w-4 text-green-600" />
                          ) : (
                            <ArrowUpRight className="h-4 w-4 text-red-600" />
                          )
                        ) : (
                          <CreditCardIcon className="h-4 w-4 text-blue-600" />
                        )}
                        <Badge variant="outline">
                          {txn.source === "wallet" ? "Wallet" : "Card"}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      {txn.source === "wallet" 
                        ? txn.description || "-" 
                        : txn.merchantName || "Card transaction"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(txn.createdAt || txn.authorizedAt).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </TableCell>
                    <TableCell className={cn(
                      "text-right font-semibold",
                      txn.source === "wallet" && txn.type === "CREDIT" ? "text-green-600" : "text-red-600"
                    )}>
                      {txn.source === "wallet" && txn.type === "CREDIT" ? "+" : "-"}
                      {formatCurrency(txn.amountCents)}
                    </TableCell>
                    <TableCell>
                      {txn.source === "card" && txn.status && (
                        <Badge variant={txn.status === "SETTLED" ? "default" : "outline"}>
                          {txn.status}
                        </Badge>
                      )}
                      {txn.source === "wallet" && (
                        <Badge variant="default">COMPLETED</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Receipt className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No transactions found</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                {search || filterType !== "all"
                  ? "Try adjusting your filters"
                  : "Transactions will appear here once you start using your cards"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
